import { Routes, Route, Navigate } from 'react-router-dom';

import { AppShell } from '@/components/layout/AppShell';
import { useSession } from '@/stores/session';
import { allNavRoutes } from '@/config/nav';
import {
  LoginPage,
  RequireAuth,
  SecurityPage,
  ForgotPasswordPage,
  ResetPasswordPage,
} from '@/features/auth';
import { OnboardPage } from '@/features/onboarding';
import {
  CompaniesListPage,
  CompanyDetailPage,
  CompanyFormPage,
} from '@/features/crm/companies';
import {
  ContactsListPage,
  ContactDetailPage,
  ContactFormPage,
} from '@/features/crm/contacts';
import {
  DealsListPage,
  DealsBoardPage,
  DealDetailPage,
  DealFormPage,
} from '@/features/crm/deals';
import { ActivitiesListPage } from '@/features/crm/activities';
import { LeadsListPage, LeadDetailPage, LeadFormPage } from '@/features/crm/leads';
import { IcpSegmentsPage } from '@/features/crm/icpSegments';
import { BidsListPage, BidDetailPage, BidFormPage, BidsRollupPage } from '@/features/crm/bids';
import {
  ProposalsListPage,
  ProposalDetailPage,
  ProposalFormPage,
} from '@/features/crm/proposals';
import {
  ContractsListPage,
  ContractDetailPage,
  ContractFormPage,
} from '@/features/crm/contracts';
import {
  ProjectsHomePage,
  ProjectWorkspacePage,
  ProjectWorklogsPage,
  OrgProjectDefaultsPage,
  ProjectFormPage,
  AllocationsPage,
  ProjectsBudgetPage,
  ProjectsReportsPage,
  WorkItemDetailPage,
} from '@/features/projects';
import { WikiHomePage } from '@/features/wiki';
import { KnowledgeBasePage } from '@/features/knowledge_base';
import {
  AiTeamPage,
  AiTeamResultsPage,
  AgentsAdminPage,
  AgentDetailPage,
  AgentResultsPage,
} from '@/features/agents';
import {
  PdpsPage,
  PdpDetailPage,
  PdpFormPage,
  SkillsMatrixPage,
} from '@/features/hrm/development';
import { DepartmentsPage } from '@/features/hrm/departments';
import { OrgChartPage } from '@/features/hrm/org-chart';
import {
  EmployeesListPage,
  EmployeeDetailPage,
  EmployeeFormPage,
  EmployeeBankAccountFormPage,
} from '@/features/hrm/employees';
import {
  OpeningsPage,
  OpeningDetailPage,
  NewOpeningPage,
  CandidatesPage,
  CandidateDetailPage,
  NewCandidatePage,
  InterviewsPage,
  InterviewDetailPage,
  NewInterviewPage,
  OffersPage,
  OfferDetailPage,
  NewOfferPage,
} from '@/features/hrm/recruitment';
import { GuidesHubPage } from '@/features/guides';
import { TimeOffListPage, TimeOffDetailPage, TimeOffFormPage } from '@/features/hrm/time-off';
import {
  CabinetProfilePage,
  EmployeeRecordPage,
  CabinetSalaryPage,
  CabinetPayslipsPage,
  CabinetBankAccountsPage,
  CabinetReviewsPage,
  CabinetReviewDetailPage,
  CabinetPdpPage,
  CabinetPdpDetailPage,
  CabinetTimeOffPage,
  CabinetOnboardingPage,
  CabinetOnboardingDetailPage,
  CabinetSkillsPage,
  CabinetInvoicesPage,
  CabinetInvoiceDetailPage,
  CabinetCommissionsPage,
  CabinetDocumentsPage,
} from '@/features/cabinet';
import {
  StaffInvoicesListPage,
  StaffInvoiceDetailPage,
  StaffInvoiceFormPage,
} from '@/features/finance/staff-invoices';
import {
  OnboardingListPage,
  OnboardingDetailPage,
  OnboardingFormPage,
} from '@/features/hrm/onboarding';
import { ReviewsListPage, ReviewDetailPage, ReviewFormPage } from '@/features/hrm/reviews';
import {
  ServicesListPage,
  ServiceDetailPage,
  ServiceFormPage,
} from '@/features/finance/services';
import {
  InvoicesListPage,
  InvoiceDetailPage,
  InvoiceFormPage,
} from '@/features/finance/invoices';
import { IncomeListPage, ExpenseListPage } from '@/features/finance/ledger';
import { CommissionsListPage } from '@/features/finance/commissions';
import {
  RecurringExpensesListPage,
  RecurringExpenseFormPage,
} from '@/features/finance/recurring-expenses';
import {
  PayrollListPage,
  PayrollDetailPage,
  PayrollFormPage,
} from '@/features/finance/payroll';
import { TaxesListPage } from '@/features/finance/taxes';
import { BudgetingPage } from '@/features/finance/budgeting';
import { FinanceReportsPage } from '@/features/finance/reports';
import {
  CoursesListPage,
  CourseDetailPage,
  CourseFormPage,
} from '@/features/lms/courses';
import { MaterialsLibraryPage } from '@/features/lms/materials';
import { AssessmentsListPage } from '@/features/lms/assessments';
import {
  CommandCenterPage,
  UpworkAnalyticsPage,
  LinkedInAnalyticsPage,
  WebsiteAnalyticsPage,
  MarketTrendsPage,
  NewsFeedPage,
} from '@/features/command-center';
import {
  AnalyticsDashboardPage,
  ExportsPage,
  KpisGoalsPage,
} from '@/features/analytics';
import {
  OrganizationSettingsPage,
  MembersListPage,
  AuditLogPage,
  IntegrationsPage,
  ApiKeysPage,
} from '@/features/settings';
import { NotificationPreferencesPage } from '@/features/notifications';
import { CustomFieldDefsPage } from '@/features/custom-fields';
import { NotesListPage } from '@/features/notes';
import { TasksListPage, TaskDetailPage } from '@/features/tasks';
import { CalendarPage } from '@/features/calendar';
import { TemplatesListPage, DocumentsListPage } from '@/features/documents';
import { Dashboard } from '@/pages/Dashboard';
import { NotFound } from '@/pages/NotFound';
import { Placeholder } from '@/pages/Placeholder';

// Nav destinations without a real feature yet render a placeholder. Companies
// (first built slice) is excluded — it has real routes below.
const placeholderRoutes = allNavRoutes().filter(
  (r) =>
    r.path !== '/' &&
    !r.path.startsWith('/crm/companies') &&
    !r.path.startsWith('/crm/contacts') &&
    !r.path.startsWith('/crm/deals') &&
    !r.path.startsWith('/crm/leads') &&
    !r.path.startsWith('/crm/bids') &&
    !r.path.startsWith('/crm/proposals') &&
    !r.path.startsWith('/crm/contracts') &&
    r.path !== '/crm/activities' &&
    r.path !== '/crm/icp-segments' &&
    // Projects entity + sub-pages handled below.
    r.path !== '/projects' &&
    r.path !== '/projects/resources' &&
    r.path !== '/projects/budget' &&
    r.path !== '/projects/reports' &&
    r.path !== '/wiki' &&
    r.path !== '/knowledge-base' &&
    !r.path.startsWith('/ai-team') &&
    // HRM Employees + Time Off + Onboarding + Reviews + Recruitment handled below; others stay placeholders.
    !r.path.startsWith('/hrm/employees') &&
    !r.path.startsWith('/hrm/recruitment') &&
    !r.path.startsWith('/hrm/time-off') &&
    !r.path.startsWith('/hrm/onboarding') &&
    !r.path.startsWith('/hrm/reviews') &&
    !r.path.startsWith('/hrm/pdp') &&
    r.path !== '/hrm/skills' &&
    r.path !== '/hrm/departments' &&
    r.path !== '/hrm/org-chart' &&
    // Finance pages handled below; remaining Finance sub-pages stay placeholders.
    r.path !== '/finance/catalog' &&
    r.path !== '/finance/billing' &&
    !r.path.startsWith('/finance/staff-invoices') &&
    r.path !== '/finance/commissions' &&
    !r.path.startsWith('/finance/recurring-expenses') &&
    r.path !== '/finance/income' &&
    r.path !== '/finance/expenses' &&
    !r.path.startsWith('/finance/payroll') &&
    r.path !== '/finance/taxes' &&
    r.path !== '/finance/budgeting' &&
    r.path !== '/finance/reports' &&
    // LMS Courses + Materials/Guides/Assessments handled below; Education stays a placeholder.
    r.path !== '/lms/courses' &&
    r.path !== '/lms/materials' &&
    r.path !== '/lms/guides' &&
    r.path !== '/lms/assessments' &&
    // Command Center exec dashboard + sub-pages handled below.
    !r.path.startsWith('/command-center') &&
    // Analytics: dashboard + KPIs + Exports handled below.
    r.path !== '/analytics' &&
    r.path !== '/analytics/kpis' &&
    r.path !== '/analytics/exports' &&
    // Settings sub-pages handled below.
    r.path !== '/settings/organization' &&
    r.path !== '/settings/members' &&
    r.path !== '/settings/integrations' &&
    r.path !== '/settings/notifications/preferences' &&
    r.path !== '/settings/custom-fields' &&
    r.path !== '/settings/project-defaults' &&
    r.path !== '/settings/api-keys' &&
    r.path !== '/settings/agents' &&
    r.path !== '/settings/audit' &&
    // My Space (cabinet) — real routes below.
    !r.path.startsWith('/me') &&
    // Workspace › Tasks + Notes + Calendar handled below.
    r.path !== '/notes' &&
    r.path !== '/tasks' &&
    r.path !== '/calendar' &&
    // Documents: Files + Template Library handled below.
    r.path !== '/documents/files' &&
    r.path !== '/documents/templates' &&
    // Help & Guides — real route below.
    r.path !== '/guides',
);

/** Landing at `/`: Founder / Super Admin get the org-wide Dashboard; guests land
 * on Help & Guides (their entry point); every other staff member lands on My
 * Space (their own Profile). */
function Home() {
  const role = useSession((s) => s.role);
  if (role === 'super_admin' || role === 'founder') return <Dashboard />;
  if (role === 'guest') return <Navigate to="/guides" replace />;
  return <Navigate to="/me/account" replace />;
}

const App = () => {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Onboarding wizard — authenticated but outside the AppShell */}
      <Route element={<RequireAuth />}>
        <Route path="/onboard" element={<OnboardPage />} />
      </Route>

      {/* Protected */}
      <Route element={<RequireAuth />}>
        <Route element={<AppShell />}>
          <Route path="/" element={<Home />} />

          {/* CRM · Companies — first vertical slice */}
          <Route path="/crm/companies" element={<CompaniesListPage />} />
          <Route path="/crm/companies/new" element={<CompanyFormPage />} />
          <Route path="/crm/companies/:publicId" element={<CompanyDetailPage />} />
          <Route path="/crm/companies/:publicId/edit" element={<CompanyFormPage />} />

          {/* CRM · Contacts */}
          <Route path="/crm/contacts" element={<ContactsListPage />} />
          <Route path="/crm/contacts/new" element={<ContactFormPage />} />
          <Route path="/crm/contacts/:publicId" element={<ContactDetailPage />} />
          <Route path="/crm/contacts/:publicId/edit" element={<ContactFormPage />} />

          {/* CRM · Bids — the per-proposal ledger in front of Leads (DEV13-72) */}
          <Route path="/crm/bids" element={<BidsListPage />} />
          <Route path="/crm/bids/new" element={<BidFormPage />} />
          <Route path="/crm/bids/rollup" element={<BidsRollupPage />} />
          <Route path="/crm/bids/:publicId" element={<BidDetailPage />} />
          <Route path="/crm/bids/:publicId/edit" element={<BidFormPage />} />

          {/* CRM · Leads */}
          <Route path="/crm/leads" element={<LeadsListPage />} />
          <Route path="/crm/leads/new" element={<LeadFormPage />} />
          <Route path="/crm/leads/:publicId" element={<LeadDetailPage />} />
          <Route path="/crm/leads/:publicId/edit" element={<LeadFormPage />} />

          {/* CRM · Deals — Money value object + role-based masking (F3/F4) */}
          <Route path="/crm/deals" element={<DealsListPage />} />
          <Route path="/crm/deals/board" element={<DealsBoardPage />} />
          <Route path="/crm/deals/new" element={<DealFormPage />} />
          <Route path="/crm/deals/:publicId" element={<DealDetailPage />} />
          <Route path="/crm/deals/:publicId/edit" element={<DealFormPage />} />

          <Route path="/crm/activities" element={<ActivitiesListPage />} />
          <Route path="/crm/icp-segments" element={<IcpSegmentsPage />} />

          {/* CRM · Proposals — line items + per-line Money snapshots (money roles only) */}
          <Route path="/crm/proposals" element={<ProposalsListPage />} />
          <Route path="/crm/proposals/new" element={<ProposalFormPage />} />
          <Route path="/crm/proposals/:publicId" element={<ProposalDetailPage />} />
          <Route path="/crm/proposals/:publicId/edit" element={<ProposalFormPage />} />
          <Route path="/crm/contracts" element={<ContractsListPage />} />
          <Route path="/crm/contracts/new" element={<ContractFormPage />} />
          <Route path="/crm/contracts/:publicId" element={<ContractDetailPage />} />
          <Route path="/crm/contracts/:publicId/edit" element={<ContractFormPage />} />

          {/* Projects — native, write-owned module (ADR-071, replaces Plane mirror) */}
          <Route path="/projects" element={<ProjectsHomePage />} />
          <Route path="/projects/new" element={<ProjectFormPage />} />
          <Route path="/projects/:publicId" element={<ProjectWorkspacePage />} />
          <Route path="/projects/:publicId/edit" element={<ProjectFormPage />} />
          <Route path="/projects/:publicId/work-items/:itemPublicId" element={<WorkItemDetailPage />} />

          {/* Projects sub-slices — Worklogs (native), Time, Allocations, Budget, Reports, Portal, Milestones */}
          <Route path="/projects/worklogs" element={<ProjectWorklogsPage />} />
          <Route path="/projects/resources" element={<AllocationsPage />} />
          <Route path="/projects/budget" element={<ProjectsBudgetPage />} />
          <Route path="/projects/reports" element={<ProjectsReportsPage />} />

          {/* Wiki — standalone knowledge base (ADR-075), independent of project Pages */}
          <Route path="/wiki" element={<WikiHomePage />} />

          {/* Knowledge Base — org-wide RAG corpora (upload + semantic search) */}
          <Route path="/knowledge-base" element={<KnowledgeBasePage />} />

          {/* AI Team — staff-facing directory of installed agents + results (all staff) */}
          <Route path="/ai-team" element={<AiTeamPage />} />
          {/* Cross-agent results archive (static path — ranks above :code). */}
          <Route path="/ai-team/results" element={<AiTeamResultsPage />} />
          {/* Standalone agent detail (read-only) — reached from the AI Team directory. */}
          <Route path="/ai-team/:code" element={<AgentDetailPage context="team" />} />
          <Route path="/ai-team/:code/results" element={<AgentResultsPage context="team" />} />

          {/* HRM · Employees — salary/tax masked server-side (C7) */}
          <Route path="/hrm/employees" element={<EmployeesListPage />} />
          <Route path="/hrm/employees/new" element={<EmployeeFormPage />} />
          <Route path="/hrm/employees/:publicId" element={<EmployeeDetailPage />} />
          <Route path="/hrm/employees/:publicId/edit" element={<EmployeeFormPage />} />
          <Route
            path="/hrm/employees/:publicId/bank-accounts/new"
            element={<EmployeeBankAccountFormPage />}
          />
          <Route
            path="/hrm/employees/:publicId/bank-accounts/:accountPublicId/edit"
            element={<EmployeeBankAccountFormPage />}
          />

          {/* HRM Recruitment — Openings · Candidates (Kanban) · Interviews · Offers */}
          <Route path="/hrm/recruitment" element={<OpeningsPage />} />
          <Route path="/hrm/recruitment/new" element={<NewOpeningPage />} />
          <Route path="/hrm/recruitment/candidates" element={<CandidatesPage />} />
          <Route path="/hrm/recruitment/candidates/new" element={<NewCandidatePage />} />
          <Route path="/hrm/recruitment/candidates/:id" element={<CandidateDetailPage />} />
          <Route path="/hrm/recruitment/candidates/:id/edit" element={<NewCandidatePage />} />
          <Route path="/hrm/recruitment/interviews" element={<InterviewsPage />} />
          <Route path="/hrm/recruitment/interviews/new" element={<NewInterviewPage />} />
          <Route path="/hrm/recruitment/interviews/:id" element={<InterviewDetailPage />} />
          <Route path="/hrm/recruitment/interviews/:id/edit" element={<NewInterviewPage />} />
          <Route path="/hrm/recruitment/offers" element={<OffersPage />} />
          <Route path="/hrm/recruitment/offers/new" element={<NewOfferPage />} />
          <Route path="/hrm/recruitment/offers/:id" element={<OfferDetailPage />} />
          <Route path="/hrm/recruitment/offers/:id/edit" element={<NewOfferPage />} />
          <Route path="/hrm/recruitment/:id" element={<OpeningDetailPage />} />
          <Route path="/hrm/recruitment/:id/edit" element={<NewOpeningPage />} />

          {/* HRM sub-slices — Time Off, Onboarding, Reviews, PDP, Skills, Org Chart */}
          <Route path="/hrm/time-off" element={<TimeOffListPage />} />
          <Route path="/hrm/time-off/new" element={<TimeOffFormPage />} />
          <Route path="/hrm/time-off/:publicId" element={<TimeOffDetailPage />} />
          <Route path="/hrm/time-off/:publicId/edit" element={<TimeOffFormPage />} />
          <Route path="/hrm/pdp" element={<PdpsPage />} />
          <Route path="/hrm/pdp/new" element={<PdpFormPage />} />
          <Route path="/hrm/pdp/:publicId" element={<PdpDetailPage />} />
          <Route path="/hrm/pdp/:publicId/edit" element={<PdpFormPage />} />
          <Route path="/hrm/skills" element={<SkillsMatrixPage />} />
          <Route path="/hrm/departments" element={<DepartmentsPage />} />
          <Route path="/hrm/org-chart" element={<OrgChartPage />} />
          <Route path="/hrm/onboarding" element={<OnboardingListPage />} />
          <Route path="/hrm/onboarding/new" element={<OnboardingFormPage />} />
          <Route path="/hrm/onboarding/:publicId" element={<OnboardingDetailPage />} />
          <Route path="/hrm/onboarding/:publicId/edit" element={<OnboardingFormPage />} />
          <Route path="/hrm/reviews" element={<ReviewsListPage />} />
          <Route path="/hrm/reviews/new" element={<ReviewFormPage />} />
          <Route path="/hrm/reviews/:publicId" element={<ReviewDetailPage />} />
          <Route path="/hrm/reviews/:publicId/edit" element={<ReviewFormPage />} />

          {/* Finance · Service Catalog — rate card (USD base + per-currency overrides, ADR-006) */}
          <Route path="/finance/catalog" element={<ServicesListPage />} />
          <Route path="/finance/catalog/new" element={<ServiceFormPage />} />
          <Route path="/finance/catalog/:publicId" element={<ServiceDetailPage />} />
          <Route path="/finance/catalog/:publicId/edit" element={<ServiceFormPage />} />

          {/* Finance · Billing — AR/AP invoices, line items + issue (money roles only) */}
          <Route path="/finance/billing" element={<InvoicesListPage />} />
          <Route path="/finance/billing/new" element={<InvoiceFormPage />} />
          <Route path="/finance/billing/:publicId" element={<InvoiceDetailPage />} />
          <Route path="/finance/billing/:publicId/edit" element={<InvoiceFormPage />} />

          {/* Finance · Staff Invoices — hours × rate, e-signed by the employee */}
          <Route path="/finance/staff-invoices" element={<StaffInvoicesListPage />} />
          <Route path="/finance/staff-invoices/new" element={<StaffInvoiceFormPage />} />
          <Route path="/finance/staff-invoices/:publicId" element={<StaffInvoiceDetailPage />} />
          <Route path="/finance/staff-invoices/:publicId/edit" element={<StaffInvoiceFormPage />} />

          {/* Finance · Commissions — sales commission ledger (ADR-046) */}
          <Route path="/finance/commissions" element={<CommissionsListPage />} />

          {/* Finance · Recurring expenses — per-employee + company subs (ADR-047) */}
          <Route path="/finance/recurring-expenses" element={<RecurringExpensesListPage />} />
          <Route path="/finance/recurring-expenses/new" element={<RecurringExpenseFormPage />} />
          <Route
            path="/finance/recurring-expenses/:publicId/edit"
            element={<RecurringExpenseFormPage />}
          />

          {/* Finance · Income / Expenses — read-only ledgers */}
          <Route path="/finance/income" element={<IncomeListPage />} />
          <Route path="/finance/expenses" element={<ExpenseListPage />} />

          {/* Finance · Payroll — runs + payslips (salary masking C7) + finalize */}
          <Route path="/finance/payroll" element={<PayrollListPage />} />
          <Route path="/finance/payroll/new" element={<PayrollFormPage />} />
          <Route path="/finance/payroll/:publicId" element={<PayrollDetailPage />} />

          {/* Finance sub-slices — Taxes (record-only), Budgeting, Reports */}
          <Route path="/finance/taxes" element={<TaxesListPage />} />
          <Route path="/finance/budgeting" element={<BudgetingPage />} />
          <Route path="/finance/reports" element={<FinanceReportsPage />} />

          {/* LMS · Courses — lessons + self-enroll + complete (HR-gated edits) */}
          <Route path="/lms/courses" element={<CoursesListPage />} />
          <Route path="/lms/courses/new" element={<CourseFormPage />} />
          <Route path="/lms/courses/:publicId" element={<CourseDetailPage />} />
          <Route path="/lms/courses/:publicId/edit" element={<CourseFormPage />} />

          {/* LMS sub-slices — Materials library, Guides view, Assessments + attempts */}
          <Route path="/lms/materials" element={<MaterialsLibraryPage />} />
          <Route
            path="/lms/guides"
            element={<MaterialsLibraryPage defaultKind="guide" pageTitle="Guides" />}
          />
          <Route path="/lms/assessments" element={<AssessmentsListPage />} />

          {/* Workspace › Tasks + Notes + Calendar — L1 shared entities */}
          <Route path="/tasks" element={<TasksListPage />} />
          <Route path="/tasks/:publicId" element={<TaskDetailPage />} />
          <Route path="/notes" element={<NotesListPage />} />
          <Route path="/calendar" element={<CalendarPage />} />

          {/* Workspace › Documents — Template Library (CRUD + render) + rendered files */}
          <Route path="/documents/templates" element={<TemplatesListPage />} />
          <Route path="/documents/files" element={<DocumentsListPage />} />

          {/* Command Center — read-only cross-module KPI cockpit + analytics sub-pages */}
          <Route path="/command-center" element={<CommandCenterPage />} />
          <Route path="/command-center/upwork" element={<UpworkAnalyticsPage />} />
          <Route path="/command-center/linkedin" element={<LinkedInAnalyticsPage />} />
          <Route path="/command-center/website" element={<WebsiteAnalyticsPage />} />
          <Route path="/command-center/market" element={<MarketTrendsPage />} />
          <Route path="/command-center/news" element={<NewsFeedPage />} />

          {/* Analytics & BI — cross-module dashboard, OKR, on-demand exports */}
          <Route path="/analytics" element={<AnalyticsDashboardPage />} />
          <Route path="/analytics/kpis" element={<KpisGoalsPage />} />
          <Route path="/analytics/exports" element={<ExportsPage />} />

          {/* Settings (Admin) — Organization + Members; rest are placeholders */}
          <Route path="/settings/organization" element={<OrganizationSettingsPage />} />
          <Route path="/settings/members" element={<MembersListPage />} />
          <Route path="/settings/integrations" element={<IntegrationsPage />} />
          {/* Notifications inbox retired — it lives in the header bell now. */}
          {/* Wave 11 — per-user channel preferences (email/Slack/Telegram dispatch). */}
          <Route
            path="/settings/notifications/preferences"
            element={<NotificationPreferencesPage />}
          />
          {/* Wave 15 polish — admin manages CustomFieldDef rows. */}
          <Route path="/settings/custom-fields" element={<CustomFieldDefsPage />} />
          <Route path="/settings/project-defaults" element={<OrgProjectDefaultsPage />} />
          <Route path="/settings/api-keys" element={<ApiKeysPage />} />
          {/* Agents admin — install/configure per-tenant agents (Founder/Super-Admin). */}
          <Route path="/settings/agents" element={<AgentsAdminPage />} />
          {/* Standalone agent detail (management) — reached from the Agents admin list. */}
          <Route path="/settings/agents/:code" element={<AgentDetailPage context="admin" />} />
          <Route
            path="/settings/agents/:code/results"
            element={<AgentResultsPage context="admin" />}
          />
          <Route path="/settings/audit" element={<AuditLogPage />} />
          {/* Account › Security (Wave 9 — MFA enroll/disable). Available to all
              signed-in roles, since 2FA is a per-user setting, not org admin. */}
          <Route path="/account/security" element={<SecurityPage />} />

          {/* My Space — self-service cabinet for the signed-in member (all staff
              roles; data is self-scoped server-side, see apps/cabinet). */}
          <Route path="/me/account" element={<CabinetProfilePage />} />
          <Route path="/me/record" element={<EmployeeRecordPage />} />
          <Route path="/me/salary" element={<CabinetSalaryPage />} />
          <Route path="/me/payslips" element={<CabinetPayslipsPage />} />
          <Route path="/me/bank-accounts" element={<CabinetBankAccountsPage />} />
          <Route path="/me/reviews" element={<CabinetReviewsPage />} />
          <Route path="/me/reviews/:publicId" element={<CabinetReviewDetailPage />} />
          <Route path="/me/pdp" element={<CabinetPdpPage />} />
          <Route path="/me/pdp/:publicId" element={<CabinetPdpDetailPage />} />
          <Route path="/me/time-off" element={<CabinetTimeOffPage />} />
          <Route path="/me/onboarding" element={<CabinetOnboardingPage />} />
          <Route path="/me/onboarding/:publicId" element={<CabinetOnboardingDetailPage />} />
          <Route path="/me/skills" element={<CabinetSkillsPage />} />
          <Route path="/me/invoices" element={<CabinetInvoicesPage />} />
          <Route path="/me/invoices/:publicId" element={<CabinetInvoiceDetailPage />} />
          <Route path="/me/commissions" element={<CabinetCommissionsPage />} />
          <Route path="/me/documents" element={<CabinetDocumentsPage />} />

          {/* Help & Guides — standalone help hub */}
          <Route path="/guides" element={<GuidesHubPage />} />

          {placeholderRoutes.map((r) => (
            <Route key={r.path} path={r.path} element={<Placeholder title={r.title} />} />
          ))}

          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
};

export default App;
